import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type TelegramUser } from "@features/auth/hooks/useTelegramAuth";
import { authApi, type ConsentData } from "@shared/api/auth.api";
import { useBalanceStore } from "@shared/store/balance.store";
import { useHistoryStore } from "@shared/store/history.store";

export type { ConsentData };

interface User {
  id: string;
  email: string | null;
  name: string;
  picture?: string;
  role?: "user" | "superadmin";
  plan: string;
  transcriptionsUsed: number;
  transcriptionsLimit: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signInWithGoogle: (idToken: string, consents?: ConsentData) => Promise<void>;
  signInWithGoogleCode: (code: string, consents?: ConsentData) => Promise<void>;
  signInWithYandex: (accessToken: string, consents?: ConsentData) => Promise<void>;
  signInWithTelegram: (user: TelegramUser, consents?: ConsentData) => Promise<void>;
  refreshUser: () => Promise<void>;
  signOut: () => void;
  openAuthModal: (mode?: "signin" | "signup") => void;
  closeAuthModal: () => void;
  authModalOpen: boolean;
  authModalMode: "signin" | "signup";
  updateUserEmail: (email: string) => void;
  needsEmailForPayment: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signin" | "signup">("signin");

  // Только planName, а не весь баланс: подписка на объект balance перерисовывала
  // всех потребителей useAuth на каждое обновление баланса — а это WS-события,
  // refetch в шапке и поллинг раз в две секунды на странице истории
  const planName = useBalanceStore(state => state.balance?.planName);
  // Замок на один запрос профиля: StrictMode в dev монтирует эффекты дважды
  const initAuthStartedRef = useRef(false);

  // Профиль читаем ровно один раз за монтирование. Раньше эффект зависел от
  // состояния баланса, и его загрузка дёргала второй запрос /auth/profile:
  // лишний раунд-трип плюс новый объект user, перерисовывающий всё дерево
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      try {
        const profile = await authApi.getProfile();
        setUser({
          ...profile,
          plan: useBalanceStore.getState().balance?.planName || "free",
          transcriptionsUsed: 0,
          transcriptionsLimit: 3
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // Cookie может отсутствовать или быть невалидным
      }
      setIsLoading(false);
    };

    if (initAuthStartedRef.current) {
      return;
    }
    initAuthStartedRef.current = true;
    void initAuth();
  }, []);

  // Тариф приходит с балансом, то есть позже профиля: дописываем его в готовый
  // user вместо повторного запроса профиля
  useEffect(() => {
    setUser(prev => {
      if (!prev) {
        return prev;
      }
      const plan = planName || "free";
      if (prev.plan === plan) {
        return prev;
      }
      return { ...prev, plan };
    });
  }, [planName]);

  // Объявлены до входов: те вызывают closeAuthModal и держат её в зависимостях
  const openAuthModal = useCallback((mode: "signin" | "signup" = "signin"): void => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback((): void => {
    setAuthModalOpen(false);
  }, []);

  const signInWithGoogle = useCallback(
    async (idToken: string, consents?: ConsentData): Promise<void> => {
      try {
        await authApi.googleAuth(idToken, consents);

        // После успешной авторизации cookie установлена, запрашиваем актуальные данные
        const profile = await authApi.getProfile();

        // Обновляем баланс
        await useBalanceStore.getState().fetchBalance();

        const currentBalance = useBalanceStore.getState().balance;

        setUser({
          ...profile,
          plan: currentBalance?.planName || "free",
          transcriptionsUsed: 0,
          transcriptionsLimit: 3
        });

        closeAuthModal();
      } catch (error) {
        console.error("Google auth failed:", error);
        throw error;
      }
    },
    [closeAuthModal]
  );

  const signInWithGoogleCode = useCallback(
    async (code: string, consents?: ConsentData): Promise<void> => {
      try {
        await authApi.googleCodeAuth(code, consents);

        // После успешной авторизации cookie установлена, запрашиваем актуальные данные
        const profile = await authApi.getProfile();

        // Обновляем баланс
        await useBalanceStore.getState().fetchBalance();

        const currentBalance = useBalanceStore.getState().balance;

        setUser({
          ...profile,
          plan: currentBalance?.planName || "free",
          transcriptionsUsed: 0,
          transcriptionsLimit: 3
        });

        closeAuthModal();
      } catch (error) {
        console.error("Google code auth failed:", error);
        // Логируем полную структуру ошибки для отладки
        if (typeof error === "object" && error !== null && "response" in error) {
          console.error("Response data:", (error as any).response?.data);
        }
        throw error;
      }
    },
    [closeAuthModal]
  );

  const signInWithYandex = useCallback(
    async (accessToken: string, consents?: ConsentData): Promise<void> => {
      try {
        await authApi.yandexAuth(accessToken, consents);

        // После успешной авторизации cookie установлена, запрашиваем актуальные данные
        const profile = await authApi.getProfile();

        // Обновляем баланс
        await useBalanceStore.getState().fetchBalance();

        const currentBalance = useBalanceStore.getState().balance;

        setUser({
          ...profile,
          plan: currentBalance?.planName || "free",
          transcriptionsUsed: 0,
          transcriptionsLimit: 3
        });

        closeAuthModal();
      } catch (error) {
        console.error("Yandex auth failed:", error);
        throw error;
      }
    },
    [closeAuthModal]
  );

  const signInWithTelegram = useCallback(
    async (user: TelegramUser, consents?: ConsentData): Promise<void> => {
      try {
        await authApi.telegramAuth(user, consents);

        const profile = await authApi.getProfile();
        await useBalanceStore.getState().fetchBalance();
        const currentBalance = useBalanceStore.getState().balance;

        setUser({
          ...profile,
          plan: currentBalance?.planName || "free",
          transcriptionsUsed: 0,
          transcriptionsLimit: 3
        });

        closeAuthModal();
      } catch (error) {
        console.error("Telegram auth failed:", error);
        throw error;
      }
    },
    [closeAuthModal]
  );

  // Перечитывает профиль по уже установленной cookie: нужен, когда вход завершила
  // не эта вкладка (окно авторизации Яндекса)
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const profile = await authApi.getProfile();
      await useBalanceStore.getState().fetchBalance();
      const currentBalance = useBalanceStore.getState().balance;

      setUser({
        ...profile,
        plan: currentBalance?.planName || "free",
        transcriptionsUsed: 0,
        transcriptionsLimit: 3
      });

      closeAuthModal();
    } catch (error) {
      console.error("Refresh user failed:", error);
      throw error;
    }
  }, [closeAuthModal]);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      // Очищаем кеш истории и баланс при выходе: иначе неавторизованный
      // пользователь видит остатки чужого баланса и кнопка транскрибации блокируется
      useHistoryStore.getState().clearCache();
      useBalanceStore.getState().reset();
    }
  }, []);

  // Через функциональный setUser, иначе колбэк зависел бы от user и пересоздавался
  const updateUserEmail = useCallback((email: string): void => {
    setUser(prev => (prev ? { ...prev, email } : prev));
  }, []);

  // Без мемоизации значение контекста — новый объект на каждый рендер провайдера,
  // и все потребители useAuth перерисовываются вместе с ним
  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      signInWithGoogle,
      signInWithGoogleCode,
      signInWithYandex,
      signInWithTelegram,
      refreshUser,
      signOut,
      openAuthModal,
      closeAuthModal,
      authModalOpen,
      authModalMode,
      updateUserEmail,
      needsEmailForPayment: !user?.email
    }),
    [
      user,
      isLoading,
      signInWithGoogle,
      signInWithGoogleCode,
      signInWithYandex,
      signInWithTelegram,
      refreshUser,
      signOut,
      openAuthModal,
      closeAuthModal,
      authModalOpen,
      authModalMode,
      updateUserEmail
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
